import { memo, useState, useRef } from 'react';

const TRACKS = [
  { file: '/music/track1.m4a', title: "Inquiry to 'Can You Do It Cheaper'", num: 1 },
  { file: '/music/track2.m4a', title: 'Timeline Written in Pencil', num: 2 },
  { file: '/music/track3.m4a', title: 'Lav Rustle Symphony (No. One)', num: 3 },
  { file: '/music/track4.m4a', title: 'Uncle Bob (Shot List Assassin)', num: 4 },
  { file: '/music/track5.m4a', title: 'No Feed, No Cues', num: 5 },
  { file: '/music/track6.m4a', title: 'Golden Hour Is a Lie', num: 6 },
  { file: '/music/track7.m4a', title: 'Fix by Nesting', num: 7 },
  { file: '/music/track8.m4a', title: 'Make It More Us', num: 8 },
  { file: '/music/track9.m4a', title: 'Sparkler Exit, Fire Marshal Exit', num: 9 },
  { file: '/music/track10.m4a', title: 'Hard Drives & Heartbeats', num: 10 },
];

const MusicPlayer = memo(() => {
  const [trackIdx, setTrackIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const audioRef = useRef(null);

  const track = TRACKS[trackIdx];

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
    setPlaying(!playing);
  };

  const playTrack = (idx) => {
    setTrackIdx(idx);
    setPlaying(true);
    setTimeout(() => audioRef.current?.play().catch(() => {}), 100);
  };

  const nextTrack = () => playTrack((trackIdx + 1) % TRACKS.length);

  return (
    <div className="rounded-xl border overflow-hidden mt-3" style={{ borderColor: 'rgba(128,128,128,0.15)', background: 'rgba(128,128,128,0.05)' }}>
      <audio ref={audioRef} src={track.file} onEnded={nextTrack} />

      {/* Now playing bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px' }}>
        <button
          onClick={togglePlay}
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #C9A84C, #c46a4e)', border: 'none', cursor: 'pointer' }}
        >
          <span style={{ color: '#fff', fontSize: '10px', marginLeft: playing ? '0' : '1px' }}>
            {playing ? '❚❚' : '▶'}
          </span>
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-sans font-medium truncate" style={{ color: 'inherit' }}>
            {track.title}
          </p>
          <p className="text-xs font-sans" style={{ color: '#C9A84C', fontSize: '9px' }}>
            The Wedding Filmmaker's Playlist
          </p>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs flex-shrink-0"
          style={{ color: '#8c8782', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
        >
          {expanded ? '▾' : '▴'}
        </button>
      </div>

      {/* Track list — expandable */}
      {expanded && (
        <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
          {TRACKS.map((t, i) => (
            <button
              key={i}
              onClick={() => playTrack(i)}
              className="w-full flex items-center gap-3 px-3 py-2 text-left transition-all hover:bg-gh-linen"
              style={{
                background: i === trackIdx ? 'rgba(198,169,111,0.06)' : 'transparent',
                border: 'none', cursor: 'pointer',
                borderLeft: i === trackIdx ? '2px solid #C9A84C' : '2px solid transparent',
              }}
            >
              <span
                className="w-5 h-5 rounded flex items-center justify-center text-xs font-sans flex-shrink-0"
                style={{
                  background: i === trackIdx ? '#C9A84C' : 'rgba(128,128,128,0.15)',
                  color: i === trackIdx ? '#fff' : '#8c8782',
                  fontSize: '9px', fontWeight: 600,
                }}
              >
                {i === trackIdx && playing ? '♪' : t.num}
              </span>
              <span
                className="text-xs font-sans truncate"
                style={{ color: i === trackIdx ? '#C9A84C' : '#8c8782', fontWeight: i === trackIdx ? 600 : 400 }}
              >
                {t.title}
              </span>
            </button>
          ))}
        </div>
      )}
      {/* Streaming links with official logos */}
      <div style={{ display: 'flex', gap: '10px', padding: '10px 12px', borderTop: '1px solid rgba(128,128,128,0.15)', alignItems: 'center' }}>
        <a href="https://open.spotify.com/album/09i0bEdGvjkYiVVqdUWeSH" target="_blank" rel="noopener noreferrer" data-testid="spotify-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none', transition: 'opacity .2s', opacity: 0.7 }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}>
          <svg width="20" height="20" viewBox="0 0 236 226" xmlns="http://www.w3.org/2000/svg">
            <path fill="#1ED760" d="m122.37,3.31C61.99.91,11.1,47.91,8.71,108.29c-2.4,60.38,44.61,111.26,104.98,113.66,60.38,2.4,111.26-44.6,113.66-104.98C229.74,56.59,182.74,5.7,122.37,3.31Zm46.18,160.28c-1.36,2.4-4.01,3.6-6.59,3.24-.79-.11-1.58-.37-2.32-.79-14.46-8.23-30.22-13.59-46.84-15.93-16.62-2.34-33.25-1.53-49.42,2.4-3.51.85-7.04-1.3-7.89-4.81-.85-3.51,1.3-7.04,4.81-7.89,17.78-4.32,36.06-5.21,54.32-2.64,18.26,2.57,35.58,8.46,51.49,17.51,3.13,1.79,4.23,5.77,2.45,8.91Zm14.38-28.72c-2.23,4.12-7.39,5.66-11.51,3.43-16.92-9.15-35.24-15.16-54.45-17.86-19.21-2.7-38.47-1.97-57.26,2.16-1.02.22-2.03.26-3.01.12-3.41-.48-6.33-3.02-7.11-6.59-1.01-4.58,1.89-9.11,6.47-10.12,20.77-4.57,42.06-5.38,63.28-2.4,21.21,2.98,41.46,9.62,60.16,19.74,4.13,2.23,5.66,7.38,3.43,11.51Zm15.94-32.38c-2.1,4.04-6.47,6.13-10.73,5.53-1.15-.16-2.28-.52-3.37-1.08-19.7-10.25-40.92-17.02-63.07-20.13-22.15-3.11-44.42-2.45-66.18,1.97-5.66,1.15-11.17-2.51-12.32-8.16-1.15-5.66,2.51-11.17,8.16-12.32,24.1-4.89,48.74-5.62,73.25-2.18,24.51,3.44,47.99,10.94,69.81,22.29,5.12,2.66,7.11,8.97,4.45,14.09Z"/>
          </svg>
          <span style={{ fontFamily: 'var(--sans)', fontSize: '8px', color: '#8c8782', letterSpacing: '.06em', textTransform: 'uppercase' }}>Spotify</span>
        </a>
        <a href="https://music.apple.com/us/album/rough-cuts/1881805930" target="_blank" rel="noopener noreferrer" data-testid="apple-music-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none', transition: 'opacity .2s', opacity: 0.7 }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}>
          <svg width="20" height="20" viewBox="0 0 361 361" xmlns="http://www.w3.org/2000/svg">
            <defs><linearGradient id="amg" x1="180" y1="358.6" x2="180" y2="7.76" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#FA233B"/><stop offset="1" stopColor="#FB5C74"/></linearGradient></defs>
            <path fill="url(#amg)" d="M360,112.61c0-4.3,0-8.6-.02-12.9-.02-3.62-.06-7.24-.16-10.86-.21-7.89-.68-15.84-2.08-23.64-1.42-7.92-3.75-15.29-7.41-22.49-3.6-7.07-8.3-13.53-13.91-19.14-5.61-5.61-12.08-10.31-19.15-13.91-7.19-3.66-14.56-5.98-22.47-7.41-7.8-1.4-15.76-1.87-23.65-2.08-3.62-.1-7.24-.14-10.86-.16C255.99,0,251.69,0,247.39,0H112.61c-4.3,0-8.6,0-12.9.02-3.62.02-7.24.06-10.86.16C80.96.4,73,.86,65.2,2.27c-7.92,1.42-15.28,3.75-22.47,7.41-7.07,3.6-13.54,8.3-19.15,13.91-5.61,5.61-10.31,12.07-13.91,19.14-3.66,7.2-5.99,14.57-7.41,22.49-1.4,7.8-1.87,15.76-2.08,23.64-.1,3.62-.14,7.24-.16,10.86C0,104.01,0,108.31,0,112.61v134.77c0,4.3,0,8.6.02,12.9.02,3.62.06,7.24.16,10.86.21,7.89.68,15.84,2.08,23.64,1.42,7.92,3.75,15.29,7.41,22.49,3.6,7.07,8.3,13.53,13.91,19.14,5.61,5.61,12.08,10.31,19.15,13.91,7.19,3.66,14.56,5.98,22.47,7.41,7.8,1.4,15.76,1.87,23.65,2.08,3.62.1,7.24.14,10.86.16,4.3.03,8.6.02,12.9.02h134.77c4.3,0,8.6,0,12.9-.02,3.62-.02,7.24-.06,10.86-.16,7.89-.21,15.85-.68,23.65-2.08,7.92-1.42,15.28-3.75,22.47-7.41,7.07-3.6,13.54-8.3,19.15-13.91,5.61-5.61,10.31-12.07,13.91-19.14,3.66-7.2,5.99-14.57,7.41-22.49,1.4-7.8,1.87-15.76,2.08-23.64.1-3.62.14-7.24.16-10.86.03-4.3.02-8.6.02-12.9V112.61z"/>
            <path fill="#fff" d="M254.5,55c-.87.08-8.6,1.45-9.53,1.64l-107,21.59-.04.01c-2.79.59-4.98,1.58-6.67,3-2.04,1.71-3.17,4.13-3.6,6.95-.09.6-.24,1.82-.24,3.62,0,0,0,109.32,0,133.92,0,3.13-.25,6.17-2.37,8.76-2.12,2.59-4.74,3.37-7.81,3.99-2.33.47-4.66.94-6.99,1.41-8.84,1.78-14.59,2.99-19.8,5.01-4.98,1.93-8.71,4.39-11.68,7.51-5.89,6.17-8.28,14.54-7.46,22.38.7,6.69,3.71,13.09,8.88,17.82,3.49,3.2,7.85,5.63,12.99,6.66,5.33,1.07,11.01.7,19.31-.98,4.42-.89,8.56-2.28,12.5-4.61,3.9-2.3,7.24-5.37,9.85-9.11,2.62-3.75,4.31-7.92,5.24-12.35.96-4.57,1.19-8.7,1.19-13.26l0-116.15c0-6.22,1.76-7.86,6.78-9.08,0,0,88.94-17.94,93.09-18.75,5.79-1.11,8.52.54,8.52,6.61l0,79.29c0,3.14-.03,6.32-2.17,8.92-2.12,2.59-4.74,3.37-7.81,3.99-2.33.47-4.66.94-6.99,1.41-8.84,1.78-14.59,2.99-19.8,5.01-4.98,1.93-8.71,4.39-11.68,7.51-5.89,6.17-8.49,14.54-7.67,22.38.7,6.69,3.92,13.09,9.09,17.82,3.49,3.2,7.85,5.56,12.99,6.6,5.33,1.07,11.01.69,19.31-.98,4.42-.89,8.56-2.22,12.5-4.55,3.9-2.3,7.24-5.37,9.85-9.11,2.62-3.75,4.31-7.92,5.24-12.35.96-4.57,1-8.7,1-13.26V64.46C263.54,58.3,260.29,54.5,254.5,55z"/>
          </svg>
          <span style={{ fontFamily: 'var(--sans)', fontSize: '8px', color: '#8c8782', letterSpacing: '.06em', textTransform: 'uppercase' }}>Apple Music</span>
        </a>
      </div>
    </div>
  );
});

MusicPlayer.displayName = 'MusicPlayer';
export default MusicPlayer;
